import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { ArrowRight, Check } from "lucide-react";

export function WhyHarmCheck() {
  const base = import.meta.env.BASE_URL;
  return (
    <section id="why-harmcheck" className="pt-10 pb-24 md:py-24 bg-background border-b border-border/40">
      <div className="container mx-auto px-6 text-center max-w-4xl mb-16">
        <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
          One Platform.<br className="md:hidden" /> Three Core Defenses.
        </h2>
        <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
          Always-on monitoring or fast, on-demand review — HarmCheck deploys nearly 50 proprietary classifiers to find what matters, when it matters.
        </p>
      </div>

      <div className="container mx-auto px-0 md:px-6 grid md:grid-cols-3 gap-8">
        <div id="restricted-list" className="group relative border border-primary/50 bg-card p-10 hover:border-primary transition-colors duration-500 overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl group-hover:bg-primary/10 transition-colors" />
          <div className="relative border-b border-primary/30 bg-black overflow-hidden aspect-video -mx-10 -mt-10 mb-8">
            <video
              controls
              preload="none"
              aria-label="HarmCheck Restricted List product demo video"
              poster={`${base}videos/restricted-list-home-poster-1052.webp`}
              className="w-full h-full"
            >
              <source src={`${base}videos/restricted-list-home.mp4`} type="video/mp4" />
            </video>
          </div>
          <h3 className="text-2xl md:text-3xl font-bold tracking-tight mb-6">Restricted List<span className="text-base align-super">™</span></h3>
          <h4 className="text-xl md:text-[22px] font-bold tracking-normal leading-tight mb-4 text-primary-text">Stop insider trading<br />before it’s sent.</h4>
          <p className="text-muted-foreground mb-8">
            HarmCheck monitors email against your firm’s restricted securities list in real time — flagging MNPI and references to restricted tickers, issuers, and related entities before a message is sent.
          </p>
          <ul className="space-y-4 mb-10">
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <span className="text-base">Monitors for MNPI against your firm’s restricted list</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <span className="text-base">Flags restricted tickers, issuers, and related entities</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <span className="text-base">Real-time alerts the moment a match appears</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <span className="text-base">Works inside email — no workflow changes</span>
            </li>
          </ul>
          <Link href="/restricted-list" className="inline-flex items-center p-0 text-primary-text font-medium text-base group/btn hover:underline">
            Explore Restricted List <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
          </Link>
        </div>

        <div id="active-listening" className="group relative border border-primary/50 bg-card p-10 hover:border-primary transition-colors duration-500 overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl group-hover:bg-primary/10 transition-colors" />
          <div className="relative border-b border-primary/30 bg-black overflow-hidden aspect-video -mx-10 -mt-10 mb-8">
            <video
              controls
              preload="none"
              aria-label="HarmCheck Active Listening product demo video"
              poster={`${base}videos/active-listening-poster-1052.webp`}
              className="w-full h-full"
            >
              <source src={`${base}videos/active-listening.mp4`} type="video/mp4" />
            </video>
          </div>
          <h3 className="text-2xl md:text-3xl font-bold tracking-tight mb-6">Active Listening<span className="text-base align-super">™</span></h3>
          <h4 className="text-xl md:text-[22px] font-bold tracking-normal leading-tight md:leading-normal mb-4 text-primary-text">Catch risk before it’s sent.</h4>
          <p className="text-muted-foreground mb-8">
            Communication moves fast. Mistakes follow. HarmCheck Active Listening monitors in real time, flagging harmful or unlawful language before it’s sent—so teams can fix it before it becomes liability.
          </p>
          <ul className="space-y-4 mb-10">
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <span className="text-base">Email add-in for Outlook</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <span className="text-base">Flags unlawful language before you hit send</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <span className="text-base">Nearly 50 categories of harm detected</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <span className="text-base">Dashboard with real-time alerts</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <span className="text-base">Restricted List detection</span>
            </li>
          </ul>
          <Link href="/active-listening" className="inline-flex items-center p-0 text-primary-text font-medium text-base group/btn hover:underline">
            Explore Active Listening <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
          </Link>
        </div>

        <div id="rapid-deploy" className="group relative border border-[#ff7b03]/50 bg-card p-10 hover:border-[#ff7b03] transition-colors duration-500 overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#ff7b03]/5 rounded-full blur-3xl group-hover:bg-[#ff7b03]/10 transition-colors" />
          <div className="relative border-b border-[#ff7b03]/30 bg-black overflow-hidden aspect-video -mx-10 -mt-10 mb-8">
            <video
              controls
              preload="none"
              aria-label="HarmCheck Rapid Deploy product demo video"
              poster={`${base}videos/rapid-deploy-poster-1052.webp`}
              className="w-full h-full"
            >
              <source src={`${base}videos/rapid-deploy.mp4`} type="video/mp4" />
            </video>
          </div>
          <h3 className="text-2xl md:text-3xl font-bold tracking-tight mb-6">Rapid Deploy<span className="text-base align-super">™</span></h3>
          <h4 className="text-xl md:text-[22px] font-bold tracking-normal leading-tight md:leading-normal mb-4" style={{ color: "#ff7b03" }}>Uncover risk at scale.</h4>
          <p className="text-muted-foreground mb-8">
            HarmCheck Rapid Deploy analyzes massive datasets in hours, not weeks—saving thousands of review hours and delivering clarity when you need it most.
          </p>
          <ul className="space-y-4 mb-10">
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#ff7b03] shrink-0 mt-0.5" />
              <span className="text-base">Built to handle large datasets</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#ff7b03] shrink-0 mt-0.5" />
              <span className="text-base">Processes 20,000+ pages/ hour</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#ff7b03] shrink-0 mt-0.5" />
              <span className="text-base">Dashboard view annotated original documents</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#ff7b03] shrink-0 mt-0.5" />
              <span className="text-base">Built for fraud, audits, and pre-deposition discovery</span>
            </li>
            <li className="flex items-start gap-3">
              <Check className="w-5 h-5 text-[#ff7b03] shrink-0 mt-0.5" />
              <span className="text-base">Delivers defensible, court-ready findings — fast</span>
            </li>
          </ul>
          <Link href="/rapid-deploy" className="inline-flex items-center p-0 text-[#ff7b03] font-medium text-base group/btn hover:underline">
            Explore Rapid Deploy <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
}
